import React, { useState, useEffect, useRef, Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { HelmetProvider, Helmet } from "react-helmet-async";

// iOS-safe storage utilities
import { 
  getLocalItem, 
  setLocalItem, 
  removeLocalItem, 
  getSessionItem, 
  setSessionItem, 
  removeSessionItem 
} from "@/utils/safeStorage";

// ============== SCROLL TO TOP + GA4 PAGE VIEW TRACKING ==============
function ScrollToTop() {
  const { pathname } = useLocation();
  const isFirstRender = useRef(true);
  
  useEffect(() => {
    // iOS Safari scroll-lock recovery (P0 bug):
    // Radix Dialog/Sheet/Drawer sometimes leave the body locked when a
    // route change occurs while the modal is still open (e.g. setTimeout
    // navigate after a "success" screen). Force-clear lingering scroll
    // locks on every route change so the user can always scroll.
    try {
      const body = document.body;
      if (body) {
        if (body.hasAttribute('data-scroll-locked')) {
          body.removeAttribute('data-scroll-locked');
        }
        // Radix also writes inline styles for the lock — wipe the ones it sets
        ['overflow', 'overflowX', 'overflowY', 'paddingRight', 'marginRight', 'position', 'top', 'width'].forEach((prop) => {
          if (body.style[prop]) body.style[prop] = '';
        });
      }
      const html = document.documentElement;
      if (html && html.style.overflow) html.style.overflow = '';
    } catch (_) { /* no-op */ }

    window.scrollTo(0, 0);
    
    // GA4 SPA page view tracking (skip initial load - handled by gtag config)
    if (isFirstRender.current) {
      isFirstRender.current = false;
      // Track initial page view
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'page_view', {
          page_path: pathname,
          page_location: window.location.href
        });
      }
      return;
    }
    
    // Track subsequent route changes
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', {
        page_path: pathname,
        page_location: window.location.href
      });
    }
  }, [pathname]);
  
  return null;
}
import api, { API, getAuthToken, clearAuthToken } from "@/api";
import PrivateAccess from "@/components/PrivateAccess";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import BrowserCompatibilityBanner from "@/components/BrowserCompatibilityBanner";

// VeriClean Auth Provider (ISOLATED from VeriTrade)
import { VeriCleanAuthProvider } from "@/vericlean/VeriCleanAuthContext";
import { useVeriCleanTracking } from "@/vericlean/vericleanTracking";
import { useVeriTradeTracking } from "@/utils/veritradeTracking";
// PERF: lazy-load welcome assistants — they don't render for ~900ms after mount
const WelcomeAssistant = lazy(() => import("@/components/welcome/WelcomeAssistant"));
const VeriCleanWelcomeAssistant = lazy(() => import("@/components/welcome/VeriCleanWelcomeAssistant"));
// Helper function to check if user can access VeriQuote features (tradesperson or cleaner only)
const isVeriquoteUser = (user) => {
  if (!user) return false;
  return ['tradesperson', 'cleaner'].includes(user.user_type);
};

// LoginRedirect component - preserves signup query param
const LoginRedirect = ({ user, getRoleDashboardPath }) => {
  const location = useLocation();
  if (user) {
    return <Navigate to={getRoleDashboardPath(user)} />;
  }
  // Preserve signup param if present
  const searchParams = new URLSearchParams(location.search);
  const isSignup = searchParams.get('signup') === 'true';
  return <Navigate to={isSignup ? "/app?signup=true" : "/app?login=true"} replace />;
};

// AppRoute component - handles /app entry:
//   • Logged in → role-specific dashboard (preserved behaviour)
//   • Logged out + auth params (?login=true / ?signup=true / ?role=…) → LandingPage
//     with auth modal (preserved — this is how existing "Sign Up" / "Log In" CTAs
//     across the site funnel Trade/Customer users into the modal)
//   • Logged out + no auth params → 301-style client redirect to `/` so the
//     modern MarketingHomeV2 renders. Prevents the deprecated `MarketingHome`
//     legacy homepage (with its old £49.99 supplier pricing and dated layout)
//     from ever being served publicly at /app. (P0 route hotfix — Jul 2026.)
const AppRoute = ({ user, getRoleDashboardPath }) => {
  const location = useLocation();
  
  // If logged in, redirect to dashboard
  if (user) {
    return <Navigate to={getRoleDashboardPath(user)} />;
  }
  
  // Check for auth params
  const searchParams = new URLSearchParams(location.search);
  const hasAuthParams = searchParams.get('login') === 'true' || 
                        searchParams.get('signup') === 'true' || 
                        searchParams.get('role');
  
  // Fire trade/customer signup_clicked once per session per role, regardless of which
  // CTA entry point sent the user here. Covers all `/app?signup=true&role=...` deep links.
  if (hasAuthParams && searchParams.get('signup') === 'true') {
    try {
      const role = (searchParams.get('role') || '').toLowerCase();
      const isTrade = ['trade', 'tradesperson', 'trader'].includes(role);
      const isCustomer = role === 'customer' || role === 'homeowner';
      const eventName = isTrade
        ? 'trade_signup_clicked'
        : isCustomer
        ? 'customer_signup_clicked'
        : 'signup_clicked_unspecified';
      const flagKey = `vt_signup_clicked_fired:${eventName}`;
      if (typeof window !== 'undefined' && !window.sessionStorage.getItem(flagKey)) {
        // Lazy import to avoid breaking the route render if tracking utility throws
        import('@/utils/veritradeTracking').then((mod) => {
          try {
            mod.track(eventName, {
              metadata: {
                role: role || null,
                referrer: document.referrer || null,
                via: 'app_route_deep_link',
              },
            });
            window.sessionStorage.setItem(flagKey, '1');
          } catch { /* ignore */ }
        }).catch(() => { /* ignore */ });
      }
    } catch { /* never break the route */ }
  }
  
  // If auth params present, show LandingPage with auth forms
  if (hasAuthParams) {
    return <LandingPage />;
  }
  
  // Otherwise — logged out with no auth params — redirect to the canonical
  // modern homepage `/`. Preserves any incoming query params so campaign / UTM
  // tracking is not lost on the hop. `replace` avoids polluting Back-button
  // history with the intermediate /app URL.
  return <Navigate to={{ pathname: "/", search: location.search }} replace />;
};

// ============== EAGERLY LOADED (Landing/Marketing) ==============
// PERF: MarketingHome (~1768 LOC) is now lazy-loaded too. The Suspense fallback
// (Preserved for one-click rollback via App.js — see MarketingHomeV2 below.)
const MarketingHome = lazyRetry(() => import("@/pages/marketing/MarketingHome"));
// Phase 2 — new public homepage. Old MarketingHome kept above for rollback.
const MarketingHomeV2 = lazyRetry(() => import("@/pages/marketing/MarketingHomeV2"));
const PropertyProfessionalsLanding = lazyRetry(() => import("@/pages/marketing/PropertyProfessionalsLanding"));
const HomeownerLanding = lazyRetry(() => import("@/pages/marketing/HomeownerLanding"));
// below paints an instant lightweight skeleton so the first paint is fast
// while the marketing chunk downloads.

// ============== LAZY IMPORT WITH RETRY ==============
// iOS WebView / Facebook in-app browser can fail to load chunks due to
// aggressive caching of stale HTML that references old chunk hashes.
// Recovery strategy:
//   1. First attempt: normal import
//   2. On failure: retry once after 1.5s delay
//   3. If retry also fails: force a one-time full page reload with cache-bust
//      (guarded by sessionStorage flag to prevent infinite reload loops)
function lazyRetry(importFn) {
  return lazy(() =>
    importFn().catch(() =>
      new Promise((resolve, reject) => {
        // Retry once after short delay
        setTimeout(() => {
          importFn()
            .then(resolve)
            .catch((retryErr) => {
              // Both attempts failed — likely stale cached chunks after deployment.
              // Force a one-time full page reload to get fresh HTML + chunk manifest.
              const reloadKey = '__chunk_reload__';
              let alreadyReloaded = false;
              try { alreadyReloaded = sessionStorage.getItem(reloadKey) === '1'; } catch (_) {}

              if (!alreadyReloaded) {
                try { sessionStorage.setItem(reloadKey, '1'); } catch (_) {}
                console.warn('[lazyRetry] Chunk load failed twice — forcing page reload');
                window.location.reload();
                // Return a pending promise to keep Suspense showing while page reloads
                return new Promise(() => {});
              }

              // Already reloaded once — do not loop. Surface error to ErrorBoundary.
              try { sessionStorage.removeItem(reloadKey); } catch (_) {}
              reject(retryErr);
            });
        }, 1500);
      })
    )
  );
}

// ============== LAZY LOADED PAGES ==============
// All other pages are code-split and loaded on demand

// Auth & Landing
const LandingPage = lazyRetry(() => import("@/pages/LandingPage"));
const HomeV2 = lazyRetry(() => import("@/pages/HomeV2"));
const ResetPasswordPage = lazyRetry(() => import("@/pages/ResetPasswordPage"));
const ForgotPasswordPage = lazyRetry(() => import("@/pages/ForgotPasswordPage"));
const VeriCleanResetPassword = lazyRetry(() => import("@/pages/vericlean/VeriCleanResetPassword"));

// Dashboards
const EnhancedDashboard = lazyRetry(() => import("@/pages/EnhancedDashboard"));
// D2 — Rebuilt tradesperson dashboard (business-light shell, matches
// approved premium reference image). Rolled out at /dashboard for
// tradespeople + cleaners. `EnhancedDashboard` is kept imported for a
// safe rollback until QA completes.
const TradeDashboard = lazyRetry(() => import("@/pages/trades/TradeDashboard"));
const CustomerDashboard = lazyRetry(() => import("@/pages/CustomerDashboard"));
const SupplierDashboard = lazyRetry(() => import("@/pages/SupplierDashboard"));
// Phase S1 — modernised supplier dashboard shell + routed pages.
// Legacy SupplierDashboard (2,300-line tabbed monolith) is preserved at
// /supplier/dashboard/legacy for one-click rollback. Do not delete until S2/S3
// and final supplier QA are complete.
const SupplierDashboardEntry = lazyRetry(() => import("@/pages/supplier/SupplierDashboardEntry"));
const SupplierMaterialRequestsPage = lazyRetry(() => import("@/pages/supplier/SupplierMaterialRequestsPage"));
const SupplierQuotesPage = lazyRetry(() => import("@/pages/supplier/SupplierQuotesPage"));
const SupplierOrdersPage = lazyRetry(() => import("@/pages/supplier/SupplierOrdersPage"));
const SupplierCustomersPage = lazyRetry(() => import("@/pages/supplier/SupplierCustomersPage"));
const SupplierProfilePage = lazyRetry(() => import("@/pages/supplier/SupplierProfilePage"));
const SupplierAccountPage = lazyRetry(() => import("@/pages/supplier/SupplierAccountPage"));
const SupplierAnalyticsPage = lazyRetry(() => import("@/pages/supplier/SupplierAnalyticsPage"));
const SupplierDealsPage = lazyRetry(() => import("@/pages/supplier/SupplierDealsPage"));
const SupplierProductsPage = lazyRetry(() => import("@/pages/supplier/SupplierProductsPage"));
const SupplierDealFormPage = lazyRetry(() => import("@/pages/supplier/SupplierDealFormPage"));
const SupplierBranchesPage = lazyRetry(() => import("@/pages/supplier/SupplierBranchesPage"));
const SupplierProductFormPage = lazyRetry(() => import("@/pages/supplier/SupplierProductFormPage"));

// Public (unauthenticated) supplier surfaces + branded 404 catch-all.
// PublicSupplierProfilePage is now rendered ONLY via SupplierUrlResolver at
// the canonical /suppliers/:supplierUserId route — see P0.2 consolidation.
const SupplierProfileLegacyRedirect = lazyRetry(() => import("@/pages/public/SupplierProfileLegacyRedirect"));
const PublicSupplierDealPage = lazyRetry(() => import("@/pages/public/PublicSupplierDealPage"));
const NotFoundPage = lazyRetry(() => import("@/pages/NotFoundPage"));
const SupplierProductDetailPage = lazyRetry(() => import("@/pages/supplier/SupplierProductDetailPage"));
const SupplierProductImportPage = lazyRetry(() => import("@/pages/supplier/SupplierProductImportPage"));
const SupplierProductImportHistoryPage = lazyRetry(() => import("@/pages/supplier/SupplierProductImportHistoryPage"));
const SupplierTeamPage = lazyRetry(() => import("@/pages/supplier/SupplierTeamPage"));

// Jobs
const JobsPage = lazyRetry(() => import("@/pages/JobsPage"));
const TradeToTradePage = lazyRetry(() => import("@/pages/TradeToTradePage"));
const PostJobPage = lazyRetry(() => import("@/pages/PostJobPage"));
const PostJobFunnel = lazyRetry(() => import("@/pages/PostJobFunnel"));
const JobDetailPage = lazyRetry(() => import("@/pages/JobDetailPage"));

// Search & Contacts
const SearchPage = lazyRetry(() => import("@/pages/SearchPage"));
const ContactRequestsPage = lazyRetry(() => import("@/pages/ContactRequestsPage"));
const MessagesPage = lazyRetry(() => import("@/pages/MessagesPage"));
const NotificationsPage = lazyRetry(() => import("@/pages/NotificationsPage"));

// Profile
const ProfilePage = lazyRetry(() => import("@/pages/ProfilePage"));
const EnhancedProfilePage = lazyRetry(() => import("@/pages/EnhancedProfilePage"));
const UserProfilePage = lazyRetry(() => import("@/pages/UserProfilePage"));

// VeriQuote
// VeriQuoteComingSoon removed - page is no longer used (VeriQuote is now live)
const VeriQuoteDashboard = lazyRetry(() => import("@/pages/VeriQuoteDashboard"));
// D3 — Modernised Quotes & Invoices list. Rolled out at /veriquote.
// Legacy VeriQuoteDashboard is kept imported for a safe rollback.
const QuotesInvoicesList = lazyRetry(() => import("@/pages/trades/QuotesInvoicesList"));
const CreateQuoteProject = lazyRetry(() => import("@/pages/CreateQuoteProject"));
const CreateDocumentPage = lazyRetry(() => import("@/pages/CreateDocumentPage"));
const QuoteProjectDetail = lazyRetry(() => import("@/pages/QuoteProjectDetail"));
const VeriQuoteSettings = lazyRetry(() => import("@/pages/VeriQuoteSettings"));
const StandaloneRamsDetail = lazyRetry(() => import("@/pages/StandaloneRamsDetail"));

// Materials
const MaterialsRequestPage = lazyRetry(() => import("@/pages/MaterialsRequestPage"));

// VeriSite
// Legacy landing preserved for rollback — do not delete during Phase VeriSite V2.
const VerisiteLanding = lazyRetry(() => import("@/pages/VerisiteLanding"));
// V2 public landing (canonical /verisite as of Phase VeriSite V2, 2026-02).
const VeriSiteLandingV2 = lazyRetry(() => import("@/pages/verisite/VeriSiteLandingV2"));
const VerisiteLogin = lazyRetry(() => import("@/pages/VerisiteLogin"));
const VerisiteListings = lazyRetry(() => import("@/pages/VerisiteListings"));
const VerisiteListingDetail = lazyRetry(() => import("@/pages/VerisiteListingDetail"));
const VerisiteDashboard = lazyRetry(() => import("@/pages/VerisiteDashboard"));
const VerisitePostPositionFunnel = lazyRetry(() => import("@/pages/verisite/VerisitePostPositionFunnel"));

// Team Jobs
const TeamJobsPage = lazyRetry(() => import("@/pages/TeamJobsPage"));
const PostTeamJobPage = lazyRetry(() => import("@/pages/PostTeamJobPage"));
const TeamJobDetailPage = lazyRetry(() => import("@/pages/TeamJobDetailPage"));

// VeriTrain
const VeriTrainPage = lazyRetry(() => import("@/pages/VeriTrainPage"));
const CreateApprenticeProfile = lazyRetry(() => import("@/pages/CreateApprenticeProfile"));
const ApprenticeProfileDetailPage = lazyRetry(() => import("@/pages/ApprenticeProfileDetailPage"));
const ApprenticeDashboard = lazyRetry(() => import("@/pages/ApprenticeDashboard"));

// Admin
const AdminDashboard = lazyRetry(() => import("@/pages/AdminDashboard"));
const EnhancedAdminDashboard = lazyRetry(() => import("@/pages/EnhancedAdminDashboard"));
// Phase 1 design-system dev preview (admin only, not linked from navigation)
const VTDesignSystemPreview = lazyRetry(() => import("@/pages/dev/VTDesignSystemPreview"));
const AdminAffiliateList = lazyRetry(() => import("@/pages/admin/AdminAffiliateList"));
const AdminAffiliatePayouts = lazyRetry(() => import("@/pages/admin/AdminAffiliatePayouts"));
const AdminAffiliateDetail = lazyRetry(() => import("@/pages/admin/AdminAffiliateDetail"));
const AdminAffiliateHub = lazyRetry(() => import("@/pages/admin/AdminAffiliateHub"));
const AdminUserList = lazyRetry(() => import("@/pages/admin/AdminUserList"));
const AdminUserDetail = lazyRetry(() => import("@/pages/admin/AdminUserDetail"));

// Admin Multi-Page Structure
const VeriCleanAdminPage = lazyRetry(() => import("@/pages/admin/VeriCleanAdminPage"));
const VeriCleanAdminDashboard = lazyRetry(() => import("@/pages/admin/VeriCleanAdminDashboard"));
const VeriCleanAdminAnalyticsPage = lazyRetry(() => import("@/pages/vericlean/VeriCleanAdminAnalyticsPage"));
const VeriCleanQuotePage = lazyRetry(() => import("@/pages/vericlean/VeriCleanQuotePage"));
const VeriTradeGrowthAnalyticsPage = lazyRetry(() => import("@/pages/admin/VeriTradeGrowthAnalyticsPage"));
const VeriCleanAdminJobScreenshot = lazyRetry(() => import("@/pages/admin/VeriCleanAdminJobScreenshot"));
const VeriCleanAdminJobAdvertView = lazyRetry(() => import("@/pages/admin/VeriCleanAdminJobAdvertView"));
const SuppliersAdminPage = lazyRetry(() => import("@/pages/admin/SuppliersAdminPage"));
const SupplierActivatePage = lazyRetry(() => import("@/pages/supplier/SupplierActivatePage"));
const VeriSiteAdminPage = lazyRetry(() => import("@/pages/admin/VeriSiteAdminPage"));
const AffiliatesAdminPage = lazyRetry(() => import("@/pages/admin/AffiliatesAdminPage"));

// Trades Hub Pages
const DocumentsHub = lazyRetry(() => import("@/pages/trades/DocumentsHub"));
const TradesLeadsPage = lazyRetry(() => import("@/pages/trades/LeadsPage"));
const FinanceHub = lazyRetry(() => import("@/pages/trades/FinanceHub"));
const AccountPage = lazyRetry(() => import("@/pages/trades/AccountPage"));

// P0 self-service subscription cancellation — VeriClean + VeriSite account pages
const VeriCleanAccountPage = lazyRetry(() => import("@/pages/vericlean/VeriCleanAccountPage"));
const VeriSiteAccountPage = lazyRetry(() => import("@/pages/verisite/VeriSiteAccountPage"));

// PM1C-C2b1 — Contractor-facing trade quote invitations
const TradeQuoteInvitationsInbox = lazyRetry(() => import("@/pages/trades/quote-invitations/TradeQuoteInvitationsInbox"));
const TradeQuoteInvitationDetail = lazyRetry(() => import("@/pages/trades/quote-invitations/TradeQuoteInvitationDetail"));

// PropertyHub Professional — dedicated signup + login (reuses standard
// /auth/signup and /auth/login endpoints; no second auth system).
const PropertyHubSignup = lazyRetry(() => import("@/pages/propertyhub/PropertyHubSignup"));
const PropertyHubLogin  = lazyRetry(() => import("@/pages/propertyhub/PropertyHubLogin"));

// PM1C-C3b1 — Contractor quote submission workflow
const TradeQuoteSubmit = lazyRetry(() => import("@/pages/trades/quote-invitations/TradeQuoteSubmit"));

// PM1C-C3b2a — Contractor revision workflow
const TradeQuoteRevise = lazyRetry(() => import("@/pages/trades/quote-invitations/TradeQuoteRevise"));

// PM1C-C2b2 — Sensitive token-opening route. Mounted OUTSIDE the ordinary
// authenticated /trade/quote-invitations subtree so it can render its own
// neutral shell (no <Layout />, no organisation display, no invitation data)
// while the raw token still exists in the URL.
const TradeInvitationOpen = lazyRetry(() => import("@/pages/trades/quote-invitations/TradeInvitationOpen"));

// Settings & Notifications
const NotificationSettingsPage = lazyRetry(() => import("@/pages/NotificationSettingsPage"));
const NotificationPrompt = lazyRetry(() => import("@/components/NotificationPrompt"));

// Payment
const PaymentSuccess = lazyRetry(() => import("@/pages/PaymentSuccess"));
const PaymentCancel = lazyRetry(() => import("@/pages/PaymentCancel"));

// Billing / Trial
const StartTrialPage = lazyRetry(() => import("@/pages/billing/StartTrialPage"));
const BillingSuccessPage = lazyRetry(() => import("@/pages/billing/BillingSuccessPage"));
const TrialCancelledPage = lazyRetry(() => import("@/pages/billing/TrialCancelledPage"));

// Verification
const VerificationPage = lazyRetry(() => import("@/pages/VerificationPage"));

// VeriAssist
const AvailableTodayPage = lazyRetry(() => import("@/pages/AvailableTodayPage"));
const VeriAssistPage = lazyRetry(() => import("@/pages/VeriAssistPage"));
const VeriAssistSettingsPage = lazyRetry(() => import("@/pages/VeriAssistSettingsPage"));

// Labourers
const LabourersPage = lazyRetry(() => import("@/pages/LabourersPage"));
const LabourerProfilePage = lazyRetry(() => import("@/pages/LabourerProfilePage"));

// Marketing Pages (lazy - not needed for homepage)
const AboutUs = lazyRetry(() => import("@/pages/marketing/AboutUs"));
const Contact = lazyRetry(() => import("@/pages/marketing/Contact"));
const FAQ = lazyRetry(() => import("@/pages/marketing/FAQ"));
const PrivacyPolicy = lazyRetry(() => import("@/pages/marketing/PrivacyPolicy"));
const TermsOfService = lazyRetry(() => import("@/pages/marketing/TermsOfService"));
const TrustAndSafety = lazyRetry(() => import("@/pages/marketing/TrustAndSafety"));
const HowVerificationWorks = lazyRetry(() => import("@/pages/marketing/HowVerificationWorks"));
const CustomerProtection = lazyRetry(() => import("@/pages/marketing/CustomerProtection"));
const TradeStandards = lazyRetry(() => import("@/pages/marketing/TradeStandards"));
const VeriTradeUKPage = lazyRetry(() => import("@/pages/marketing/VeriTradeUKPage"));
const CashbackOfferPage = lazyRetry(() => import("@/pages/CashbackOfferPage"));
const CashbackTermsPage = lazyRetry(() => import("@/pages/CashbackTermsPage"));
const VeriCleanCashbackOfferPage = lazyRetry(() => import("@/pages/VeriCleanCashbackOfferPage"));
const VeriCleanCashbackTermsPage = lazyRetry(() => import("@/pages/VeriCleanCashbackTermsPage"));
const SupplierLandingPage = lazyRetry(() => import("@/pages/SupplierLandingPage"));
// Phase S4 — new redesigned public supplier landing (SupplierLandingV2). Original file
// left intact for one-click rollback: swap the /supplier route element back to
// <SupplierLandingPage />.
const SupplierLandingV2 = lazyRetry(() => import("@/pages/supplier/SupplierLandingV2"));
const SupplierLandingDispatcher = lazyRetry(() => import("@/pages/supplier/SupplierLandingDispatcher"));
// P0 auth hotfix — dedicated modern supplier login & signup pages
const SupplierLoginV2 = lazyRetry(() => import("@/pages/supplier/SupplierLoginV2"));
const SupplierSignupV2 = lazyRetry(() => import("@/pages/supplier/SupplierSignupV2"));
// Phase 3A — modern tradesperson public landing page
const TradeLandingV2 = lazyRetry(() => import("@/pages/trades/TradeLandingV2"));

// Supplier
const SupplierNotify = lazyRetry(() => import("@/pages/SupplierNotify"));
const SupplierUrlResolver = lazyRetry(() => import("@/pages/supplier/SupplierUrlResolver"));

// Public Profile Pages
const PublicProfilePage = lazyRetry(() => import("@/pages/PublicProfilePage"));
const PublicVerificationPage = lazyRetry(() => import("@/pages/PublicVerificationPage"));

// Partner Pages
const PartnersPage = lazyRetry(() => import("@/pages/partners/PartnersPage"));
const PartnerReferral = lazyRetry(() => import("@/pages/partners/PartnerReferral"));
const AffiliateOpportunities = lazyRetry(() => import("@/pages/partners/AffiliateOpportunities"));

// Admin - Partner Enquiries
const AdminPartnerEnquiries = lazyRetry(() => import("@/pages/admin/AdminPartnerEnquiries"));

// Admin - Materials Requests
const AdminMaterialsRequestsList = lazyRetry(() => import("@/pages/admin/AdminMaterialsRequestsList"));
const AdminMaterialsRequestDetail = lazyRetry(() => import("@/pages/admin/AdminMaterialsRequestDetail"));
const AdminMaterialsRequestAdvertisingView = lazyRetry(() => import("@/pages/admin/AdminMaterialsRequestAdvertisingView"));

// Admin - Street Canvassers
const AdminCanvassers = lazyRetry(() => import("@/pages/admin/AdminCanvassers"));

// Labourer post-signup profile completion
const LabourerSetup = lazyRetry(() => import("@/pages/LabourerSetup"));

// Public customer "Is your job still open?" confirmation landing page
const JobStatusCheckConfirm = lazyRetry(() => import("@/pages/JobStatusCheckConfirm"));

// Admin marketing QR code generator (shared between VeriTrade + VeriClean)
const AdminQRCodeGenerator = lazyRetry(() => import("@/pages/admin/AdminQRCodeGenerator"));

// Saved Jobs (shortlist from map view) — VT + VC
const SavedJobsPage = lazyRetry(() => import("@/pages/SavedJobsPage"));

// Admin — Growth Hub (AI marketing draft workflow)
const GrowthOverview = lazyRetry(() => import("@/pages/admin/growth/GrowthOverview"));
const GrowthDraftsQueue = lazyRetry(() => import("@/pages/admin/growth/GrowthDraftsQueue"));

// Admin — Screenshot-friendly VeriTrade job advert view (admin-gated)
const VeriTradeAdminJobAdvertView = lazyRetry(() => import("@/pages/admin/VeriTradeAdminJobAdvertView"));

// Canvasser-submitted member account setup landing page
const Activate = lazyRetry(() => import("@/pages/Activate"));

// Affiliate — Canvasser Dashboard
const CanvasserDashboard = lazyRetry(() => import("@/pages/affiliate/CanvasserDashboard"));

// Affiliate Programme (Separate from main app)
const AffiliateLogin = lazyRetry(() => import("@/pages/affiliate/AffiliateLogin"));
const AffiliateDashboard = lazyRetry(() => import("@/pages/affiliate/AffiliateDashboard"));
const AffiliateLeaderDashboard = lazyRetry(() => import("@/pages/affiliate/AffiliateLeaderDashboard"));
const AffiliateQuickEnrolmentPage = lazyRetry(() => import("@/pages/affiliate/AffiliateQuickEnrolmentPage"));
const InviteAcceptPage = lazyRetry(() => import("@/pages/affiliate/InviteAcceptPage"));
const AffiliateResetPassword = lazyRetry(() => import("@/pages/affiliate/AffiliateResetPassword"));
const AffiliateResources = lazyRetry(() => import("@/pages/affiliate/AffiliateResources"));
const AffiliateRecentJobs = lazyRetry(() => import("@/pages/affiliate/AffiliateRecentJobs"));
const AffiliatePromoteJob = lazyRetry(() => import("@/pages/affiliate/AffiliatePromoteJob"));
const AdminMessagesPage = lazyRetry(() => import("@/pages/admin/AdminMessagesPage"));
// Stage 8.1: User-based affiliate dashboard (uses standard auth)
const UserAffiliateDashboard = lazyRetry(() => import("@/pages/affiliate/UserAffiliateDashboard"));
const AffiliateRedirect = lazyRetry(() => import("@/pages/affiliate/AffiliateRedirect"));
const CustomPathRedirect = lazyRetry(() => import("@/pages/affiliate/CustomPathRedirect"));

// Admin Affiliate Resources
const AdminAffiliateResources = lazyRetry(() => import("@/pages/admin/AdminAffiliateResources"));

// Blog Pages
const BlogIndex = lazyRetry(() => import("@/pages/blog/BlogIndex"));
const BlogPost = lazyRetry(() => import("@/pages/blog/BlogPost"));

// VeriClean Landing
// Legacy landing kept imported for rollback — do not delete during Phase VeriClean V2.
const VeriCleanLanding = lazyRetry(() => import("@/pages/VeriCleanLanding"));
// V2 public landing (canonical /vericlean as of Phase VeriClean V2, 2026-02).
const VeriCleanLandingV2 = lazyRetry(() => import("@/pages/vericlean/VeriCleanLandingV2"));

// VeriClean App Pages (ISOLATED from VeriTrade)
const VeriCleanLogin = lazyRetry(() => import("@/pages/vericlean/VeriCleanLogin"));
const VeriCleanDashboard = lazyRetry(() => import("@/pages/vericlean/VeriCleanDashboard"));
const VeriCleanPostJob = lazyRetry(() => import("@/pages/vericlean/VeriCleanPostJob"));
const VeriCleanJobDetail = lazyRetry(() => import("@/pages/vericlean/VeriCleanJobDetail"));
const VeriCleanProfileEdit = lazyRetry(() => import("@/pages/vericlean/VeriCleanProfileEdit"));
const VeriCleanPublicProfile = lazyRetry(() => import("@/pages/vericlean/VeriCleanPublicProfile"));
const VeriCleanMessages = lazyRetry(() => import("@/pages/vericlean/VeriCleanMessages"));
const VeriCleanVerification = lazyRetry(() => import("@/pages/vericlean/VeriCleanVerification"));
const VeriCleanBrowseCleaners = lazyRetry(() => import("@/pages/vericlean/VeriCleanBrowseCleaners"));
const VeriCleanQuoteDashboard = lazyRetry(() => import("@/pages/vericlean/VeriCleanQuoteDashboard"));
const VeriCleanQuoteDetail = lazyRetry(() => import("@/pages/vericlean/VeriCleanQuoteDetail"));
const VeriCleanInvoiceDetail = lazyRetry(() => import("@/pages/vericlean/VeriCleanInvoiceDetail"));
const VeriCleanRamsDetail = lazyRetry(() => import("@/pages/vericlean/VeriCleanRamsDetail"));
const VeriCleanPricing = lazyRetry(() => import("@/pages/vericlean/VeriCleanPricing"));
const VeriCleanBillingSuccess = lazyRetry(() => import("@/pages/vericlean/VeriCleanBillingSuccess"));
const VeriCleanSettings = lazyRetry(() => import("@/pages/vericlean/VeriCleanSettings"));
const VeriCleanCustomersPage = lazyRetry(() => import("@/pages/vericlean/VeriCleanCustomersPage"));
const VeriCleanExpensesPage = lazyRetry(() => import("@/pages/vericlean/VeriCleanExpensesPage"));
const VeriCleanTaxDashboardPage = lazyRetry(() => import("@/pages/vericlean/VeriCleanTaxDashboardPage"));
const VeriCleanTaxReportsPage = lazyRetry(() => import("@/pages/vericlean/VeriCleanTaxReportsPage"));
const VeriCleanBookFunnel = lazyRetry(() => import("@/pages/vericlean/VeriCleanBookFunnel"));
const SeoPageRouter = lazyRetry(() => import("@/pages/seo/SeoPageRouter"));
import { SEO_SLUGS } from "@/pages/seo/seoPageData";

// VeriTrade Customers & Expenses & Tax
const CustomersPage = lazyRetry(() => import("@/pages/CustomersPage"));
const CustomerDetailPage = lazyRetry(() => import("@/pages/customers/CustomerDetailPage"));
const ExpensesPage = lazyRetry(() => import("@/pages/ExpensesPage"));
const TaxDashboardPage = lazyRetry(() => import("@/pages/TaxDashboardPage"));
const TaxReportsPage = lazyRetry(() => import("@/pages/TaxReportsPage"));

// VeriSite Pricing
const VeriSitePricing = lazyRetry(() => import("@/pages/verisite/VeriSitePricing"));

// Supplier Pricing
const SupplierPricing = lazyRetry(() => import("@/pages/suppliers/SupplierPricing"));

// Join Tradesperson Landing Page
const JoinTradespersonPage = lazyRetry(() => import("@/pages/JoinTradespersonPage"));

// Stage 8.0: Affiliate Join Pages
const JoinPage = lazyRetry(() => import("@/pages/JoinPage"));
const TrustProtectionPage = lazyRetry(() => import("@/pages/TrustProtectionPage"));

// PropertyHub Phase 1 (homeowner)
const PropertyHubLayout = lazyRetry(() => import("@/pages/property-hub/PropertyHubLayout"));

// Property Management (PM1A — professional portal, greenfield)
const PropertyManagementShell = lazyRetry(() => import("@/pages/property-management/PropertyManagementShell"));
const PMAuthIntercept = lazyRetry(() => import("@/pages/property-management/AuthIntercept"));
const PMOnboarding = lazyRetry(() => import("@/pages/property-management/ProfessionalOnboarding"));
const PMDashboard = lazyRetry(() => import("@/pages/property-management/PortfolioDashboard"));
const PMProperties = lazyRetry(() => import("@/pages/property-management/PortfolioProperties"));
const PMClients = lazyRetry(() => import("@/pages/property-management/ClientsPage"));
const PMTeam = lazyRetry(() => import("@/pages/property-management/TeamPage"));
const PMSettings = lazyRetry(() => import("@/pages/property-management/ProfessionalSettings"));
// PM1B.1
const PMAudit = lazyRetry(() => import("@/pages/property-management/AuditLogPage"));
const PMInvitationAccept = lazyRetry(() => import("@/pages/property-management/InvitationAcceptPage"));
// PM1B.2A(i)
const PMOccupants = lazyRetry(() => import("@/pages/property-management/OccupantsPage"));
const PMComplianceCentre = lazyRetry(() => import("@/pages/property-management/compliance/ComplianceCentre"));
const PMPropertyCompliance = lazyRetry(() => import("@/pages/property-management/compliance/PropertyCompliance"));
const PMPortfolioChecklistPrint = lazyRetry(() =>
  import("@/pages/property-management/compliance/ChecklistPrint").then(m => ({ default: m.PortfolioChecklistPrint })));
const PMPropertyChecklistPrint = lazyRetry(() =>
  import("@/pages/property-management/compliance/ChecklistPrint").then(m => ({ default: m.PropertyChecklistPrint })));
const PMOnboardingChecklistPrint = lazyRetry(() =>
  import("@/pages/property-management/compliance/ChecklistPrint").then(m => ({ default: m.OnboardingChecklistPrint })));
const PMMaintenanceCentre = lazyRetry(() => import("@/pages/property-management/maintenance/MaintenanceCentre"));
const PMReportingLinksPage = lazyRetry(() => import("@/pages/property-management/maintenance/ReportingLinksPage"));
const PMQuoteCentre = lazyRetry(() => import("@/pages/property-management/quotes/QuoteCentre"));
const PMQuotePrintPack = lazyRetry(() => import("@/pages/property-management/quotes/QuotePrintPack"));
const PublicReportForm = lazyRetry(() => import("@/pages/public/PublicReportForm"));
const PMMaintenanceRequestPrint = lazyRetry(() =>
  import("@/pages/property-management/maintenance/MaintenancePrint").then(m => ({ default: m.RequestPrint })));
const PMMaintenanceWorkOrderPrint = lazyRetry(() =>
  import("@/pages/property-management/maintenance/MaintenancePrint").then(m => ({ default: m.WorkOrderPrint })));
const PMMaintenanceCombinedPrint = lazyRetry(() =>
  import("@/pages/property-management/maintenance/MaintenancePrint").then(m => ({ default: m.CombinedRecordPrint })));
const PropertyHubDashboard = lazyRetry(() => import("@/pages/property-hub/PropertyHubDashboard"));
const PropertyHubList = lazyRetry(() => import("@/pages/property-hub/PropertyList"));
const PropertyHubDetail = lazyRetry(() => import("@/pages/property-hub/PropertyDetail"));
const PropertyHubReminders    = React.lazy(() => import("@/pages/property-hub/PropertyHubSections").then(m => ({ default: m.Reminders })));
const PropertyHubMaintenance  = React.lazy(() => import("@/pages/property-hub/PropertyHubSections").then(m => ({ default: m.Maintenance })));
const PropertyHubDocuments    = React.lazy(() => import("@/pages/property-hub/PropertyHubSections").then(m => ({ default: m.Documents })));
const PropertyHubCertificates = React.lazy(() => import("@/pages/property-hub/PropertyHubSections").then(m => ({ default: m.Certificates })));
const PropertyHubProblems     = React.lazy(() => import("@/pages/property-hub/PropertyHubSections").then(m => ({ default: m.Problems })));
const PropertyHubProjects     = React.lazy(() => import("@/pages/property-hub/PropertyHubSections").then(m => ({ default: m.Projects })));
const PropertyHubContacts     = React.lazy(() => import("@/pages/property-hub/PropertyHubSections").then(m => ({ default: m.Contacts })));
const PropertyHubUtilities    = React.lazy(() => import("@/pages/property-hub/PropertyHubSections").then(m => ({ default: m.Utilities })));
const PropertyHubEnergy       = React.lazy(() => import("@/pages/property-hub/PropertyHubSections").then(m => ({ default: m.Energy })));
const PropertyHubExpenses     = React.lazy(() => import("@/pages/property-hub/PropertyHubSections").then(m => ({ default: m.Expenses })));
const PropertyHubReports      = React.lazy(() => import("@/pages/property-hub/PropertyHubSections").then(m => ({ default: m.Reports })));

// ============== ROLE-BASED REDIRECT HELPER ==============
// This function determines the correct dashboard based on user role
const getRoleDashboardPath = (user) => {
  if (!user) return '/app';
  
  // Admin users go to admin dashboard
  if (user.is_admin) {
    return '/admin';
  }
  
  // Site agents go to VeriSite dashboard
  if (user.user_type === 'site_agent') {
    return '/verisite/dashboard';
  }
  
  // Suppliers go to supplier dashboard
  if (user.is_supplier) {
    return '/supplier/dashboard';
  }
  
  // Apprentices go to apprentice dashboard
  if (user.is_apprentice) {
    return '/apprentice/dashboard';
  }
  
  // Cleaners can go to their dashboard (VeriClean auth handles this separately)
  // But if they login via main app, they go to regular dashboard
  if (user.user_type === 'cleaner') {
    return '/dashboard';
  }
  
  // Tradesperson and others go to main dashboard
  return '/dashboard';
};

// ============== SUSPENSE FALLBACK ==============
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
  </div>
);

// ============== AUTH CONTEXT ==============
export const AuthContext = React.createContext();

// ============== FIREBASE LAZY LOADER ==============
// Firebase is only loaded when user is authenticated
let firebaseListenerPromise = null;
const setupFirebaseListener = async (user, navigate) => {
  if (!user) return;
  
  try {
    // Dynamically import Firebase messaging only when needed
    const { onMessageListener } = await import("@/services/firebaseMessaging");
    
    onMessageListener()
      .then((payload) => {
        console.log('Received foreground message:', payload);
        toast(payload.notification?.title || 'New Notification', {
          description: payload.notification?.body || '',
          action: payload.data?.link ? {
            label: 'View',
            onClick: () => navigate(payload.data.link)
          } : undefined
        });
      })
      .catch((err) => console.log('Failed to listen for messages:', err));
  } catch (err) {
    console.log('Firebase messaging not available:', err);
  }
};

// ============== PUBLIC ROUTE WRAPPER ==============
function PublicWithAuth({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    checkExistingSession();
  }, []);

  const checkExistingSession = async () => {
    // CRITICAL: Reset user state before checking session
    setUser(null);
    
    try {
      // ALWAYS fetch user from backend - never trust cached data
      // Timeout after 8s to prevent hanging in slow WebView environments
      const response = await api.get('/auth/me', { timeout: 8000 });
      // Set user state ONLY from fresh backend response
      setUser(response.data);
    } catch (error) {
      console.log('Not authenticated - continuing as guest');
      // Clear potentially stale auth data on network/auth failure
      try { clearAuthToken(); } catch (_) { /* ignore */ }
      // Ensure user state is null
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  // Function to refresh user data from backend
  const refreshUser = async () => {
    try {
      const response = await api.get("/auth/me");
      setUser(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to refresh user:', error);
      return null;
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout", {});
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      // CRITICAL: Clear ALL auth tokens and session data
      clearAuthToken();
      // Clear legacy token keys
      removeLocalItem('token');
      removeLocalItem('user');
      removeSessionItem('token');
      removeSessionItem('user');
      // Clear VeriClean token as well for complete isolation
      removeLocalItem('vericlean_auth_token');
      // Clear all session storage (safe for iOS WebView)
      try { sessionStorage.clear(); } catch (_) { /* ignore */ }
      console.log('[Auth] Logout complete - all session data cleared');
      setUser(null);
      navigate('/');
    }
  };

  if (loading) {
    return <PageLoader />;
  }

  return (
    <AuthContext.Provider value={{ user, setUser, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

// ============== AUTH HANDLER ==============
function AuthHandler() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const firebaseInitialized = useRef(false);

  // VeriClean campaign analytics — auto-fires pageviews on /vericlean/* routes.
  // (Moved out of AuthHandler — see VeriCleanTrackingMounter in App() instead.)

  // Broadcast VeriTrade auth user to the Welcome Assistant mounter (which
  // lives outside AuthHandler's route subtree). One-line pub/sub avoids
  // prop-drilling through dozens of Route elements.
  useEffect(() => {
    try {
      window.__vt_user = user;
      window.dispatchEvent(new CustomEvent('vt_auth_user', { detail: user }));
    } catch { /* ignore */ }
  }, [user]);

  useEffect(() => {
    const hash = window.location.hash;
    const params = new URLSearchParams(hash.substring(1));
    const sessionId = params.get('session_id');

    if (sessionId) {
      processSessionId(sessionId);
    } else {
      checkExistingSession();
    }
  }, []);

  // Lazy-load Firebase messaging only after user is authenticated
  useEffect(() => {
    if (user && !firebaseInitialized.current) {
      firebaseInitialized.current = true;
      setupFirebaseListener(user, navigate);
    }
  }, [user, navigate]);

  const processSessionId = async (sessionId) => {
    // CRITICAL: Reset user state before processing new session
    setUser(null);
    
    try {
      const response = await api.post("/auth/session", {}, {
        headers: { 'X-Session-ID': sessionId },
        withCredentials: true,
        timeout: 10000,
      });
      const userData = response.data.user;
      // Set user state ONLY from fresh backend response
      setUser(userData);
      window.history.replaceState({}, document.title, window.location.pathname);
      // Use role-based redirect instead of generic /dashboard
      const dashboardPath = getRoleDashboardPath(userData);
      console.log('[Auth] Role-based redirect to:', dashboardPath, 'for user_type:', userData?.user_type);
      navigate(dashboardPath);
    } catch (error) {
      console.error('Session processing failed:', error);
      // Ensure user state is null on failure
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const checkExistingSession = async () => {
    // CRITICAL: Reset user state to null before checking session
    // This ensures no stale user data persists between sessions
    setUser(null);
    
    // Check token using iOS-safe method (includes in-memory fallback)
    const token = getAuthToken();
    console.log('[Auth] Checking session. Token exists:', !!token, 'Path:', location.pathname);
    
    // Define public routes that don't require authentication
    const publicRoutes = [
      '/login',
      '/signup',
      '/app',
      '/post-job',
      '/verisite/login',
      '/verisite/signup',
      '/verisite',
      '/forgot-password',
      '/reset-password',
      '/vericlean/login',
      '/vericlean/forgot-password',
      '/vericlean/reset-password',
      '/affiliate/login',
      '/affiliate/forgot-password',
      '/affiliate/reset-password',
      '/affiliate/dashboard',
      '/affiliate/canvasser',
      '/affiliate/resources',
      '/affiliate/jobs',
      '/supplier',
      '/supplier-notify',
      '/trades',
      '/trades/',
      '/activate',
      '/jobs/status-check',
      // PropertyHub Professional dedicated signup/login (Feb 2026)
      '/propertyhub',
      '/propertyhub/signup',
      '/propertyhub/login',
      // PM1C-C2b2 — Sensitive quote-invitation opener. Public in the sense
      // that it renders BEFORE authentication (it owns its own inline login
      // form); the token is meaningless without a matching authenticated
      // contractor identity so this cannot leak anything on its own.
      '/trade/quote-invitations/open',
    ];
    
    // Check if current path is a public route
    const isPublicRoute = publicRoutes.some(route => 
      location.pathname === route || location.pathname.startsWith(route + '/')
    );
    
    // If no token exists and on public route, don't make API call — EXCEPT
    // for the dedicated PropertyHub auth pages, which need to know whether
    // the visitor is already logged in so they can auto-forward to the
    // Professional dashboard instead of showing a redundant sign-in form.
    // VeriTrade issues session cookies as well as bearer tokens, so a quick
    // /auth/me is safe even when localStorage is empty.
    const isPropertyHubAuthRoute = location.pathname === '/propertyhub/login'
      || location.pathname === '/propertyhub/signup'
      || location.pathname === '/propertyhub';
    if (!token && isPublicRoute && !isPropertyHubAuthRoute) {
      console.log('[Auth] No token, allowing public route:', location.pathname);
      setLoading(false);
      return;
    }
    
    try {
      // ALWAYS fetch user from backend - never trust cached data
      // Timeout after 8s to prevent hanging in slow WebView environments
      const response = await api.get("/auth/me", { timeout: 8000 });
      console.log('[Auth] Session valid, user:', response.data.name, 'type:', response.data.user_type);
      // Set user state ONLY from fresh backend response
      setUser(response.data);
      if (location.pathname === '/' || location.pathname === '/app') {
        // Use role-based redirect instead of generic /dashboard
        const dashboardPath = getRoleDashboardPath(response.data);
        console.log('[Auth] Role-based redirect to:', dashboardPath);
        navigate(dashboardPath);
      }
    } catch (error) {
      console.log('[Auth] Not authenticated:', error.response?.status, error.friendlyMessage || error.response?.data);
      // Clear any stale tokens
      clearAuthToken();
      // Ensure user state is null
      setUser(null);
      // Only redirect to /app if NOT on a public route
      if (!isPublicRoute) {
        console.log('[Auth] Redirecting to login from protected route:', location.pathname);
        navigate('/app');
      } else {
        console.log('[Auth] Allowing access to public route:', location.pathname);
      }
    } finally {
      setLoading(false);
    }
  };

  // Function to refresh user data from backend
  const refreshUser = async () => {
    try {
      const response = await api.get("/auth/me");
      console.log('[Auth] User refreshed:', response.data.subscription_tier);
      setUser(response.data);
      return response.data;
    } catch (error) {
      console.error('[Auth] Failed to refresh user:', error);
      return null;
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout", {});
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      // CRITICAL: Clear ALL auth tokens and session data
      clearAuthToken();
      // Clear legacy token keys
      removeLocalItem('token');
      removeLocalItem('user');
      removeSessionItem('token');
      removeSessionItem('user');
      // Clear VeriClean token as well for complete isolation
      removeLocalItem('vericlean_auth_token');
      // Clear all session storage (safe for iOS WebView)
      try { sessionStorage.clear(); } catch (_) { /* ignore */ }
      console.log('[Auth] Logout complete - all session data cleared');
      setUser(null);
      navigate('/');
    }
  };

  if (loading) {
    return <PageLoader />;
  }

  return (
    <AuthContext.Provider value={{ user, setUser, logout, refreshUser }}>
      {/* Notification prompt for logged-in users - lazy loaded */}
      {user && (
        <Suspense fallback={null}>
          <NotificationPrompt />
        </Suspense>
      )}
      
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* App Routes (behind private access) */}
          {/* Role-based redirect: site_agent -> /verisite/dashboard, supplier -> /supplier/dashboard, others -> /dashboard */}
          <Route path="/app" element={<AppRoute user={user} getRoleDashboardPath={getRoleDashboardPath} />} />
          <Route path="/signup" element={user ? <Navigate to={getRoleDashboardPath(user)} /> : <Navigate to="/app?signup=true" replace />} />
          <Route path="/login" element={<LoginRedirect user={user} getRoleDashboardPath={getRoleDashboardPath} />} />
          <Route path="/activate" element={<Activate />} />
          <Route path="/jobs/status-check/:token/:action" element={<JobStatusCheckConfirm /> } />

          {/* PropertyHub Phase 1 — homeowner routes.
              Layout enforces auth (redirects to /app?login=true if unauthenticated).
              Nested routes render inside the layout's <Outlet />. */}
          <Route path="/property-hub" element={<PropertyHubLayout />}>
            <Route index element={<Navigate to="/property-hub/dashboard" replace />} />
            <Route path="dashboard"           element={<PropertyHubDashboard />} />
            <Route path="properties"          element={<PropertyHubList />} />
            <Route path="properties/:propertyId" element={<PropertyHubDetail />} />
            <Route path="maintenance"         element={<PropertyHubMaintenance />} />
            <Route path="documents"           element={<PropertyHubDocuments />} />
            <Route path="certificates"        element={<PropertyHubCertificates />} />
            <Route path="problems"            element={<PropertyHubProblems />} />
            <Route path="projects"            element={<PropertyHubProjects />} />
            <Route path="contacts"            element={<PropertyHubContacts />} />
            <Route path="utilities"           element={<PropertyHubUtilities />} />
            <Route path="energy"              element={<PropertyHubEnergy />} />
            <Route path="expenses"            element={<PropertyHubExpenses />} />
            <Route path="reminders"           element={<PropertyHubReminders />} />
            <Route path="reports"             element={<PropertyHubReports />} />
          </Route>
          {/* Property Management (PM1A — professional portal) */}
          <Route path="/property-management/auth" element={<PMAuthIntercept />} />
          <Route path="/property-management/invitations/:token" element={<PMInvitationAccept />} />
          <Route path="/property-management" element={<PropertyManagementShell />}>
            <Route index element={<Navigate to="/property-management/dashboard" replace />} />
            <Route path="onboarding" element={<PMOnboarding />} />
            <Route path="dashboard" element={<PMDashboard />} />
            <Route path="properties" element={<PMProperties />} />
            <Route path="clients" element={<PMClients />} />
            <Route path="occupants" element={<PMOccupants />} />
            <Route path="compliance" element={<PMComplianceCentre />} />
            <Route path="compliance/print/portfolio" element={<PMPortfolioChecklistPrint />} />
            <Route path="compliance/print/onboarding" element={<PMOnboardingChecklistPrint />} />
            <Route path="properties/:pid/compliance" element={<PMPropertyCompliance />} />
            <Route path="properties/:pid/compliance/print" element={<PMPropertyChecklistPrint />} />
            <Route path="maintenance" element={<PMMaintenanceCentre />} />
            <Route path="maintenance/reporting-links" element={<PMReportingLinksPage />} />
            <Route path="maintenance/requests/:rid" element={<PMMaintenanceCentre />} />
            <Route path="maintenance/work-orders/:wid" element={<PMMaintenanceCentre />} />
            <Route path="quotes" element={<PMQuoteCentre />} />
            <Route path="quotes/requests" element={<PMQuoteCentre />} />
            <Route path="quotes/requests/:qrId" element={<PMQuoteCentre />} />
            <Route path="quotes/requests/:qrId/print" element={<PMQuotePrintPack />} />
            <Route path="quotes/contractors" element={<PMQuoteCentre />} />
            <Route path="maintenance/requests/:rid/print" element={<PMMaintenanceRequestPrint />} />
            <Route path="maintenance/requests/:rid/print/complete" element={<PMMaintenanceCombinedPrint />} />
            <Route path="maintenance/work-orders/:wid/print" element={<PMMaintenanceWorkOrderPrint />} />
            <Route path="team" element={<PMTeam />} />
            <Route path="audit" element={<PMAudit />} />
            <Route path="settings" element={<PMSettings />} />
          </Route>
          <Route path="/dashboard" element={
            user ? (
              // If site_agent tries to access /dashboard, redirect to their proper dashboard
              user.user_type === 'site_agent' ? <Navigate to="/verisite/dashboard" /> :
              // If supplier tries to access /dashboard, redirect to their proper dashboard
              user.is_supplier ? <Navigate to="/supplier/dashboard" /> :
              // If apprentice tries to access /dashboard, redirect to their proper dashboard
              user.is_apprentice ? <Navigate to="/apprentice/dashboard" /> :
              // Tradesperson and cleaner get the D2 rebuilt trade dashboard
              (user.user_type === "tradesperson" || user.user_type === "cleaner") ? <TradeDashboard /> : 
              // Customers get CustomerDashboard
              <CustomerDashboard />
            ) : (
              <Navigate to="/app" />
            )
          } />
          <Route path="/jobs" element={user ? <JobsPage /> : <Navigate to="/app" />} />
          <Route path="/trade-to-trade" element={user ? <TradeToTradePage /> : <Navigate to="/app" />} />
          <Route path="/jobs/:jobId" element={user ? <JobDetailPage /> : <Navigate to="/app" />} />
          <Route path="/post-job" element={user ? <PostJobPage /> : <PostJobFunnel />} />

          {/* Trades Hub Pages */}
          <Route path="/documents" element={isVeriquoteUser(user) ? <DocumentsHub /> : <Navigate to="/dashboard" />} />
          <Route path="/leads" element={user ? <TradesLeadsPage /> : <Navigate to="/app" />} />
          <Route path="/finance" element={isVeriquoteUser(user) ? <FinanceHub /> : <Navigate to="/dashboard" />} />
          <Route path="/account" element={user ? <AccountPage /> : <Navigate to="/app" />} />

          {/* P0 self-service subscription cancellation — VeriClean / VeriSite */}
          <Route path="/vericlean/account" element={user ? <VeriCleanAccountPage /> : <Navigate to="/app" />} />
          <Route path="/verisite/account" element={user ? <VeriSiteAccountPage /> : <Navigate to="/app" />} />

          {/* PropertyHub Professional — dedicated signup + login. These
              routes reuse the standard /auth/* backend endpoints and are
              deliberately mounted OUTSIDE any auth guard so existing users
              and new users can sign in / register through the same UI. */}
          <Route path="/propertyhub/signup" element={<PropertyHubSignup />} />
          <Route path="/propertyhub/login" element={<PropertyHubLogin />} />
          <Route path="/propertyhub" element={<Navigate to="/propertyhub/signup" replace />} />

          {/* PM1C-C2b2 — Sensitive token-opening route. MUST be listed BEFORE the
              canonical /:invitationId route so the more-specific path wins.
              Route intentionally has NO Navigate guard — it renders its own
              neutral shell and performs its own in-place authentication. */}
          <Route path="/trade/quote-invitations/open/:token" element={<TradeInvitationOpen />} />

          {/* PM1C-C2b1 — Contractor-facing Trade Quote Invitations. Tradespeople + cleaners only. */}
          <Route path="/trade/quote-invitations" element={isVeriquoteUser(user) ? <TradeQuoteInvitationsInbox /> : <Navigate to="/app?login=true" />} />
          <Route path="/trade/quote-invitations/:invitationId" element={isVeriquoteUser(user) ? <TradeQuoteInvitationDetail /> : <Navigate to="/app?login=true" />} />

          {/* PM1C-C3b1 — Contractor quote submission workflow. Tradespeople + cleaners only. */}
          <Route path="/trade/quote-invitations/:invitationId/quote" element={isVeriquoteUser(user) ? <TradeQuoteSubmit /> : <Navigate to="/app?login=true" />} />

          {/* PM1C-C3b2a — Contractor revision route. Tradespeople + cleaners only. */}
          <Route path="/trade/quote-invitations/:invitationId/quote/revise" element={isVeriquoteUser(user) ? <TradeQuoteRevise /> : <Navigate to="/app?login=true" />} />

          {/* VeriQuote Routes - Only tradesperson and cleaner roles */}
          <Route path="/veriquote" element={isVeriquoteUser(user) ? <QuotesInvoicesList /> : <Navigate to="/dashboard" />} />
          <Route path="/veriquote/create" element={isVeriquoteUser(user) ? <CreateDocumentPage /> : <Navigate to="/dashboard" />} />
          <Route path="/veriquote/new" element={isVeriquoteUser(user) ? <CreateDocumentPage /> : <Navigate to="/dashboard" />} />
          <Route path="/veriquote/edit/:editId" element={isVeriquoteUser(user) ? <CreateDocumentPage /> : <Navigate to="/dashboard" />} />
          <Route path="/veriquote/project/:projectId" element={isVeriquoteUser(user) ? <QuoteProjectDetail /> : <Navigate to="/dashboard" />} />
          <Route path="/veriquote/rams/:ramsId" element={isVeriquoteUser(user) ? <StandaloneRamsDetail /> : <Navigate to="/dashboard" />} />
          <Route path="/veriquote/settings" element={isVeriquoteUser(user) ? <VeriQuoteSettings /> : <Navigate to="/dashboard" />} />
          
          {/* Customers Route - Only tradesperson and cleaner roles (VeriTrade) */}
          <Route path="/customers" element={isVeriquoteUser(user) ? <CustomersPage /> : <Navigate to="/dashboard" />} />
          <Route path="/customers/:customerId" element={isVeriquoteUser(user) ? <CustomerDetailPage /> : <Navigate to="/dashboard" />} />
          
          {/* Expenses Route - Only tradesperson and cleaner roles (VeriTrade) */}
          <Route path="/expenses" element={isVeriquoteUser(user) ? <ExpensesPage /> : <Navigate to="/dashboard" />} />
          
          {/* Tax Dashboard Route - Only tradesperson and cleaner roles (VeriTrade) */}
          <Route path="/tax" element={isVeriquoteUser(user) ? <TaxDashboardPage /> : <Navigate to="/dashboard" />} />
          
          {/* Tax Reports Route - Only tradesperson and cleaner roles (VeriTrade) */}
          <Route path="/tax-reports" element={isVeriquoteUser(user) ? <TaxReportsPage /> : <Navigate to="/dashboard" />} />
          
          {/* Materials Request Route - Only tradesperson and cleaner roles */}
          <Route path="/get-materials" element={isVeriquoteUser(user) ? <MaterialsRequestPage /> : <Navigate to="/dashboard" />} />
          
          {/* VeriSite Routes */}
          {/* Phase VeriSite V2: /verisite serves the new public landing page.
              Legacy `VerisiteLanding` is kept imported for rollback only. */}
          <Route path="/verisite" element={<VeriSiteLandingV2 />} />
          <Route path="/verisite/post-position" element={<VerisitePostPositionFunnel />} />
          <Route path="/verisite/login" element={<VerisiteLogin />} />
          <Route path="/verisite/listings" element={user ? <VerisiteListings /> : <Navigate to="/app" />} />
          <Route path="/verisite/listings/:listingId" element={user ? <VerisiteListingDetail /> : <Navigate to="/app" />} />
          {/* Site Agent Dashboard - redirects non-site-agents to VeriSite Jobs */}
          <Route path="/verisite/dashboard" element={user?.user_type === 'site_agent' ? <VerisiteDashboard /> : <Navigate to="/verisite/listings" replace />} />
          <Route path="/verisite/pricing" element={user?.user_type === 'site_agent' ? <VeriSitePricing /> : <Navigate to="/verisite/listings" replace />} />
          {/* VeriSite custom affiliate path — must be AFTER static VeriSite routes so static wins */}
          <Route path="/verisite/:customPath" element={<CustomPathRedirect section="verisite" />} />
          
          {/* Team Jobs Routes - Only tradesperson and cleaner roles */}
          <Route path="/team-jobs" element={isVeriquoteUser(user) ? <TeamJobsPage /> : <Navigate to="/dashboard" />} />
          <Route path="/team-jobs/post" element={isVeriquoteUser(user) ? <PostTeamJobPage /> : <Navigate to="/dashboard" />} />
          <Route path="/team-jobs/:jobId" element={isVeriquoteUser(user) ? <TeamJobDetailPage /> : <Navigate to="/dashboard" />} />
          {/* VeriTrain Routes - Only tradesperson and cleaner roles */}
          <Route path="/veritrain" element={isVeriquoteUser(user) ? <VeriTrainPage /> : <Navigate to="/dashboard" />} />
          <Route path="/veritrain/create-profile" element={user ? <CreateApprenticeProfile /> : <Navigate to="/dashboard" />} />
          <Route path="/veritrain/my-profile" element={user ? <CreateApprenticeProfile /> : <Navigate to="/dashboard" />} />
          <Route path="/veritrain/profile/:profileId" element={isVeriquoteUser(user) ? <ApprenticeProfileDetailPage /> : <Navigate to="/dashboard" />} />
          
          {/* Apprentice Dashboard - Only apprentice users */}
          <Route path="/apprentice/dashboard" element={user?.is_apprentice ? <ApprenticeDashboard /> : <Navigate to="/dashboard" />} />
          {/* Labourers Routes - Only tradesperson and cleaner roles */}
          <Route path="/labourers" element={isVeriquoteUser(user) ? <LabourersPage /> : <Navigate to="/dashboard" />} />
          <Route path="/labourers/:labourerId" element={isVeriquoteUser(user) ? <LabourerProfilePage /> : <Navigate to="/dashboard" />} />
          <Route path="/labourer/setup" element={user ? <LabourerSetup /> : <Navigate to="/login" />} />
          {/* /suppliers/:supplierUserId is now defined at the top-level Routes via
              SupplierUrlResolver (below), which dispatches UUIDs → SupplierProfilePage
              and custom paths → CustomPathRedirect. Kept here as a comment for
              maintainers who might otherwise look for it inside AuthHandler. */}
          <Route path="/search" element={user ? <SearchPage /> : <Navigate to="/app" />} />
          <Route path="/contact-requests" element={user ? <ContactRequestsPage /> : <Navigate to="/app" />} />
          <Route path="/messages" element={user ? <MessagesPage /> : <Navigate to="/app" />} />
          <Route path="/notifications" element={user ? <NotificationsPage /> : <Navigate to="/app?login=true" />} />
          <Route path="/profile" element={user ? <EnhancedProfilePage /> : <Navigate to="/app" />} />
          <Route path="/trust-protection" element={user ? <TrustProtectionPage /> : <Navigate to="/app" />} />
          {/* Verification Route - Only tradesperson and cleaner roles */}
          <Route path="/verification" element={isVeriquoteUser(user) ? <VerificationPage /> : <Navigate to="/dashboard" />} />
          <Route path="/veriassist-settings" element={user ? <VeriAssistSettingsPage /> : <Navigate to="/app" />} />
          <Route path="/notification-settings" element={user ? <NotificationSettingsPage /> : <Navigate to="/app" />} />
          {/* Fallback routes for missing pages - redirect to valid pages */}
          <Route path="/settings" element={<Navigate to="/notification-settings" replace />} />
          <Route path="/app/finance/invoices" element={<Navigate to="/customers" replace />} />
          <Route path="/app/finance/customers" element={<Navigate to="/customers" replace />} />
          <Route path="/app/finance/expenses" element={<Navigate to="/expenses" replace />} />
          <Route path="/app/finance/quotes" element={<Navigate to="/veriquote" replace />} />
          <Route path="/payment-success" element={user ? <PaymentSuccess /> : <Navigate to="/app" />} />
          <Route path="/payment-cancel" element={user ? <PaymentCancel /> : <Navigate to="/app" />} />
          {/* Billing / Trial Routes */}
          <Route path="/billing/start-trial" element={user ? <StartTrialPage /> : <Navigate to="/app" />} />
          <Route path="/billing/success" element={user ? <BillingSuccessPage /> : <Navigate to="/app" />} />
          <Route path="/billing/cancelled" element={user ? <TrialCancelledPage /> : <Navigate to="/app" />} />
          <Route path="/user/:userId" element={user ? <UserProfilePage /> : <Navigate to="/app" />} />
          <Route path="/admin" element={user?.is_admin ? <EnhancedAdminDashboard /> : <Navigate to="/dashboard" />} />
          {/* Phase 1 — design-system dev preview (admin-only, no nav link) */}
          <Route path="/dev/design-system" element={user?.is_admin ? <VTDesignSystemPreview /> : <Navigate to="/dashboard" />} />
          <Route path="/admin/veritrade" element={user?.is_admin ? <EnhancedAdminDashboard /> : <Navigate to="/dashboard" />} />
          <Route path="/admin/messages" element={user?.is_admin ? <AdminMessagesPage /> : <Navigate to="/dashboard" />} />
          <Route path="/admin/vericlean" element={user?.is_admin ? <VeriCleanAdminPage /> : <Navigate to="/dashboard" />} />
          <Route path="/admin/veritrade/growth" element={user?.is_admin ? <VeriTradeGrowthAnalyticsPage /> : <Navigate to="/dashboard" />} />
          <Route path="/admin/suppliers" element={user?.is_admin ? <SuppliersAdminPage /> : <Navigate to="/dashboard" />} />
          <Route path="/admin/verisite" element={user?.is_admin ? <VeriSiteAdminPage /> : <Navigate to="/dashboard" />} />
          <Route path="/admin/affiliates-page" element={user?.is_admin ? <AffiliatesAdminPage /> : <Navigate to="/dashboard" />} />
          <Route path="/admin/affiliate-hub" element={user?.is_admin ? <AdminAffiliateHub /> : <Navigate to="/dashboard" />} />
          <Route path="/admin/affiliates" element={user?.is_admin ? <AdminAffiliateList /> : <Navigate to="/dashboard" />} />
          <Route path="/admin/affiliates/:affiliateId" element={user?.is_admin ? <AdminAffiliateDetail /> : <Navigate to="/dashboard" />} />
          <Route path="/admin/affiliate-payouts" element={user?.is_admin ? <AdminAffiliatePayouts /> : <Navigate to="/dashboard" />} />
          <Route path="/admin/affiliate-resources" element={user?.is_admin ? <AdminAffiliateResources /> : <Navigate to="/dashboard" />} />
          <Route path="/admin/users" element={user?.is_admin ? <AdminUserList /> : <Navigate to="/dashboard" />} />
          <Route path="/admin/users/:userId" element={user?.is_admin ? <AdminUserDetail /> : <Navigate to="/dashboard" />} />
          <Route path="/admin/partner-enquiries" element={user?.is_admin ? <AdminPartnerEnquiries /> : <Navigate to="/dashboard" />} />
          <Route path="/admin/materials-requests" element={user?.is_admin ? <AdminMaterialsRequestsList /> : <Navigate to="/dashboard" />} />
          <Route path="/admin/materials-requests/:requestId/advertising-view" element={user?.is_admin ? <AdminMaterialsRequestAdvertisingView /> : <Navigate to="/dashboard" />} />
          <Route path="/admin/materials-requests/:requestId" element={user?.is_admin ? <AdminMaterialsRequestDetail /> : <Navigate to="/dashboard" />} />
          <Route path="/admin/canvassers" element={user?.is_admin ? <AdminCanvassers /> : <Navigate to="/dashboard" />} />
          <Route path="/admin/qr-codes" element={user?.is_admin ? <AdminQRCodeGenerator initialProduct="veritrade" backTo="/admin" /> : <Navigate to="/dashboard" />} />
          <Route path="/saved-jobs" element={user ? <SavedJobsPage product="veritrade" listEndpoint="/saved-jobs" toggleEndpoint="/saved-jobs" accent="orange" title="Saved jobs" backTo="/jobs" /> : <Navigate to="/login" />} />
          <Route path="/vericlean/saved-jobs" element={
            <VeriCleanAuthProvider>
              <SavedJobsPage product="vericlean" listEndpoint="/vericlean/saved-jobs" toggleEndpoint="/vericlean/saved-jobs" accent="sky" title="Saved cleaning jobs" backTo="/vericlean/dashboard" />
            </VeriCleanAuthProvider>
          } />
          <Route path="/admin/growth" element={user?.is_admin ? <GrowthOverview /> : <Navigate to="/dashboard" />} />
          <Route path="/admin/growth/drafts" element={user?.is_admin ? <GrowthDraftsQueue /> : <Navigate to="/dashboard" />} />
          <Route path="/admin/jobs/:jobId/advert-view" element={user?.is_admin ? <VeriTradeAdminJobAdvertView /> : <Navigate to="/dashboard" />} />
          <Route path="/affiliate/canvasser" element={<CanvasserDashboard />} />
          {/* Supplier Routes - Supplier only access */}
          {/* Phase S1 — canonical /supplier/dashboard now renders the modernised
              SupplierShell + panel dashboard. Legacy tab-based dashboard is
              preserved at /supplier/dashboard/legacy for rollback only. */}
          <Route path="/supplier/dashboard" element={
            user?.is_supplier ? <SupplierDashboardEntry /> : <Navigate to="/supplier" />
          } />
          <Route path="/supplier/dashboard/legacy" element={
            user?.is_supplier ? <SupplierDashboard /> : <Navigate to="/supplier" />
          } />
          <Route path="/supplier/material-requests" element={
            user?.is_supplier ? <SupplierMaterialRequestsPage /> : <Navigate to="/supplier" />
          } />
          <Route path="/supplier/quotes" element={
            user?.is_supplier ? <SupplierQuotesPage /> : <Navigate to="/supplier" />
          } />
          <Route path="/supplier/orders" element={
            user?.is_supplier ? <SupplierOrdersPage /> : <Navigate to="/supplier" />
          } />
          <Route path="/supplier/customers" element={
            user?.is_supplier ? <SupplierCustomersPage /> : <Navigate to="/supplier" />
          } />
          <Route path="/supplier/profile" element={
            user?.is_supplier ? <SupplierProfilePage /> : <Navigate to="/supplier" />
          } />
          <Route path="/supplier/account" element={
            user?.is_supplier ? <SupplierAccountPage /> : <Navigate to="/supplier" />
          } />
          <Route path="/supplier/analytics" element={
            user?.is_supplier ? <SupplierAnalyticsPage /> : <Navigate to="/supplier" />
          } />
          <Route path="/supplier/deals" element={
            user?.is_supplier ? <SupplierDealsPage /> : <Navigate to="/supplier" />
          } />
          <Route path="/supplier/deals/new" element={
            user?.is_supplier ? <SupplierDealFormPage /> : <Navigate to="/supplier" />
          } />
          <Route path="/supplier/deals/:dealId/edit" element={
            user?.is_supplier ? <SupplierDealFormPage /> : <Navigate to="/supplier" />
          } />
          <Route path="/supplier/branches" element={
            user?.is_supplier ? <SupplierBranchesPage /> : <Navigate to="/supplier" />
          } />
          <Route path="/supplier/products" element={
            user?.is_supplier ? <SupplierProductsPage /> : <Navigate to="/supplier" />
          } />
          <Route path="/supplier/products/new" element={
            user?.is_supplier ? <SupplierProductFormPage /> : <Navigate to="/supplier" />
          } />
          <Route path="/supplier/products/import" element={
            user?.is_supplier ? <SupplierProductImportPage /> : <Navigate to="/supplier" />
          } />
          <Route path="/supplier/products/import/:importId" element={
            user?.is_supplier ? <SupplierProductImportPage /> : <Navigate to="/supplier" />
          } />
          <Route path="/supplier/products/import-history" element={
            user?.is_supplier ? <SupplierProductImportHistoryPage /> : <Navigate to="/supplier" />
          } />
          <Route path="/supplier/products/:productId/edit" element={
            user?.is_supplier ? <SupplierProductFormPage /> : <Navigate to="/supplier" />
          } />
          <Route path="/supplier/products/:productId" element={
            user?.is_supplier ? <SupplierProductDetailPage /> : <Navigate to="/supplier" />
          } />
          <Route path="/supplier/team" element={
            user?.is_supplier ? <SupplierTeamPage /> : <Navigate to="/supplier" />
          } />
          {/* /suppliers/pricing is now defined at the top-level Routes above
              (before the /suppliers/:customPath affiliate catch-all) so that
              CustomPathRedirect does not swallow it. Kept here as a comment for
              maintainers who might otherwise look for it inside AuthHandler. */}
          {/* Public activation flow — reached via the welcome email */}
          <Route path="/supplier/activate" element={<SupplierActivatePage />} />
          {/* P0.2 — Singular /supplier/:supplierUserId is now a pure
              query-preserving redirect to the canonical plural URL
              /suppliers/:supplierUserId. This kills the duplicate profile
              render and duplicate API fetch that came from mapping the
              new PublicSupplierProfilePage to two routes. See
              SupplierProfileLegacyRedirect.jsx for details. Must remain
              AFTER every specific /supplier/* route (dashboard, deals,
              activate, etc.) so it does not shadow them. */}
          <Route path="/supplier/:supplierUserId" element={<SupplierProfileLegacyRedirect />} />
          {/* Branded catch-all — no more blank white pages. */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </AuthContext.Provider>
  );
}

// ============== VERICLEAN TRACKING MOUNTER ==============
// Lives inside <BrowserRouter> but ABOVE <Routes> so it sees every route
// change regardless of which <Route> matches. The hook itself has an
// internal `if (!pathname.startsWith('/vericlean')) return;` guard, so it's
// cheap on non-VeriClean pages.
function VeriCleanTrackingMounter() {
  useVeriCleanTracking();
  return null;
}

// ============== VERITRADE TRACKING MOUNTER ==============
// Mirrors VeriClean mounter — auto-fires pageviews on public VeriTrade
// marketing routes (and skips dashboards/admin/private surfaces).
function VeriTradeTrackingMounter() {
  useVeriTradeTracking();
  return null;
}

// ============== VERITRADE WELCOME MOUNTER ==============
// Mounted once at the BrowserRouter root so it can decide based on path +
// auth whether to fire the Welcome Assistant. Receives the VeriTrade `user`
// from AuthHandler via a window-level pub/sub to avoid prop-drilling through
// the whole route tree. Falls back gracefully when user state is unknown.
function VtWelcomeMounter() {
  const [user, setUser] = React.useState(null);
  React.useEffect(() => {
    // Listen for auth state pushes from AuthHandler
    const handler = (e) => setUser(e.detail || null);
    window.addEventListener('vt_auth_user', handler);
    // Also accept any existing snapshot
    if (window.__vt_user) setUser(window.__vt_user);
    return () => window.removeEventListener('vt_auth_user', handler);
  }, []);
  return (
    <Suspense fallback={null}>
      <WelcomeAssistant user={user} />
    </Suspense>
  );
}

// ============== VERICLEAN WELCOME MOUNTER ==============
// VeriClean assistant only fires on `/vericlean` marketing route. We don't
// have direct access to VeriCleanAuth context here (it's only mounted inside
// the /vericlean/* route subtree), so we treat any non-VeriClean route as
// "anonymous" — that's fine because the path guard already restricts it.
function VcWelcomeMounter() {
  return (
    <Suspense fallback={null}>
      <VeriCleanWelcomeAssistant vericleanAccount={null} />
    </Suspense>
  );
}

// ============== MAIN APP ==============
function App() {
  // Track visit guard - prevents double call in StrictMode
  const visitTracked = useRef(false);

  useEffect(() => {
    // Guard against double invocation in StrictMode
    if (visitTracked.current) return;
    visitTracked.current = true;

    const trackVisit = async () => {
      try {
        await api.post("/track-visit", { page: window.location.pathname });
      } catch (error) {
        console.log('Visit tracking failed (non-critical):', error);
      }
    };
    trackVisit();
  }, []);

  return (
    <HelmetProvider>
    <div className="App">
      <Helmet defaultTitle="VeriTrade UK | Find Trusted Tradespeople &amp; Jobs">
        {/* App-level Helmet only sets the fallback document title.
             Route-level <Helmet> declarations own canonical + og:* + twitter:*. */}
      </Helmet>
      <BrowserRouter>
        <ScrollToTop />
        <BrowserCompatibilityBanner />
        <VeriCleanTrackingMounter />
        <VeriTradeTrackingMounter />
        <VtWelcomeMounter />
        <VcWelcomeMounter />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public Marketing Routes - Homepage eagerly loaded */}
            <Route path="/" element={<MarketingHomeV2 />} />
            <Route path="/report/:token" element={<PublicReportForm />} />
            <Route path="/homeowners" element={<HomeownerLanding />} />
            <Route path="/property-professionals" element={<PropertyProfessionalsLanding />} />
            <Route path="/home-v2" element={<HomeV2 />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/trust-and-safety" element={<TrustAndSafety />} />
            <Route path="/how-verification-works" element={<HowVerificationWorks />} />
            <Route path="/customer-protection" element={<CustomerProtection />} />
            <Route path="/trade-standards" element={<TradeStandards />} />
            <Route path="/veritrade-uk" element={<VeriTradeUKPage />} />
            
            {/* Cashback Campaign Pages */}
            <Route path="/cashback-offer" element={<CashbackOfferPage />} />
            <Route path="/cashback-offer/terms" element={<CashbackTermsPage />} />
            <Route path="/vericlean-cashback-offer" element={<VeriCleanCashbackOfferPage />} />
            <Route path="/vericlean-cashback-offer/terms" element={<VeriCleanCashbackTermsPage />} />
            
            {/* Tradesperson Landing/Pricing Page */}
            <Route path="/pricing" element={<JoinTradespersonPage />} />
            
            {/* Stage 8.0: Affiliate Join Pages */}
            <Route path="/join/:product" element={<JoinPage />} />
            
            {/* Blog Routes */}
            <Route path="/blog" element={<BlogIndex />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            
            {/* Public Profile Pages (Promote Your Business) */}
            <Route path="/pro/:slug" element={<PublicProfilePage />} />
            <Route path="/verify/:verificationId" element={<PublicVerificationPage />} />
            
            {/* Partner Pages */}
            <Route path="/partners" element={<PartnersPage />} />
            <Route path="/partners/affiliate" element={<AffiliateOpportunities />} />
            <Route path="/partner/:partnerId" element={<PartnerReferral />} />
            
            {/* Affiliate Programme Routes - Completely separate auth */}
            <Route path="/affiliate/login" element={<AffiliateLogin />} />
            <Route path="/affiliate/dashboard" element={<AffiliateDashboard />} />
            <Route path="/affiliate/quick-enrolment" element={<AffiliateQuickEnrolmentPage />} />
            <Route path="/affiliate/leader-dashboard" element={<AffiliateLeaderDashboard />} />
            <Route path="/affiliate/join" element={<InviteAcceptPage />} />
            <Route path="/affiliate/reset-password" element={<AffiliateResetPassword />} />
            <Route path="/affiliate/resources" element={<AffiliateResources />} />
            <Route path="/affiliate/jobs" element={<AffiliateRecentJobs />} />
            <Route path="/affiliate/jobs/:jobId/promote" element={<AffiliatePromoteJob />} />
            
            {/* Stage 8.1: User-based affiliate dashboard (standard user auth) */}
            <Route path="/my-affiliate" element={<UserAffiliateDashboard />} />
            
            {/* Clean affiliate share route — social-safe attribution */}
            <Route path="/a/:affiliateCode" element={<AffiliateRedirect />} />
            <Route path="/a/:affiliateCode/:product" element={<AffiliateRedirect />} />
            {/* Static routes that live under a `/{section}/:customPath` namespace
                MUST be declared BEFORE the affiliate custom-path catch-alls below,
                otherwise React Router matches the customPath route first and the
                static page never mounts. Also wrap in <PublicWithAuth> so the
                mounted component still has access to AuthContext. */}
            <Route path="/suppliers/pricing" element={<PublicWithAuth><SupplierPricing /></PublicWithAuth>} />
            {/* Phase S3.5 — SupplierUrlResolver distinguishes UUIDs (profile page)
                from custom affiliate paths. Does NOT need PublicWithAuth: the
                profile page is publicly navigable and the backend enforces access
                on GET /api/suppliers/:id/profile. Removing PublicWithAuth here
                eliminates a race between the async /auth/me probe and the resolver's
                render that was causing a URL flip to /app in iter_139. */}
            <Route path="/suppliers/:supplierUserId" element={<SupplierUrlResolver />} />
            {/* P0.3 — Public deal detail route. Canonical shared URL for a
                specific supplier deal. Backend enforces visibility rules
                (draft/paused → 404; scheduled-not-yet-live → 404; expired →
                410; missing → 404) so guessing an id can never reveal a
                private deal. */}
            <Route path="/suppliers/:supplierUserId/deals/:dealId" element={<PublicSupplierDealPage />} />
            <Route path="/veritrade/:customPath" element={<CustomPathRedirect section="veritrade" />} />
            <Route path="/vericlean/:customPath" element={<CustomPathRedirect section="vericlean" />} />
            
            {/* VeriClean Landing Page */}
            <Route path="/vericlean" element={<VeriCleanLandingV2 />} />
            {/* VeriClean Book/Quote Funnel - PUBLIC, no auth required */}
            <Route path="/vericlean/book" element={<VeriCleanBookFunnel />} />
            <Route path="/vericlean/quote" element={<VeriCleanQuotePage />} />
            
            {/* VeriClean App Routes - ISOLATED from VeriTrade, uses VeriCleanAuthProvider */}
            <Route path="/vericlean/login" element={
              <VeriCleanAuthProvider>
                <VeriCleanLogin />
              </VeriCleanAuthProvider>
            } />
            <Route path="/vericlean/dashboard" element={
              <VeriCleanAuthProvider>
                <VeriCleanDashboard />
              </VeriCleanAuthProvider>
            } />
            <Route path="/vericlean/admin" element={
              <VeriCleanAuthProvider>
                <VeriCleanAdminDashboard />
              </VeriCleanAuthProvider>
            } />
            <Route path="/vericlean/admin/analytics" element={
              <VeriCleanAuthProvider>
                <VeriCleanAdminAnalyticsPage />
              </VeriCleanAuthProvider>
            } />
            <Route path="/vericlean/admin/jobs/:jobId" element={
              <VeriCleanAuthProvider>
                <VeriCleanAdminJobScreenshot />
              </VeriCleanAuthProvider>
            } />
            <Route path="/vericlean/admin/job-view/:jobId" element={
              <VeriCleanAuthProvider>
                <VeriCleanAdminJobAdvertView />
              </VeriCleanAuthProvider>
            } />
            <Route path="/vericlean/admin/qr-codes" element={
              <VeriCleanAuthProvider>
                <AdminQRCodeGenerator initialProduct="vericlean" backTo="/vericlean/admin" />
              </VeriCleanAuthProvider>
            } />
            <Route path="/vericlean/post-job" element={
              <VeriCleanAuthProvider>
                <VeriCleanPostJob />
              </VeriCleanAuthProvider>
            } />
            <Route path="/vericlean/jobs/:jobId" element={
              <VeriCleanAuthProvider>
                <VeriCleanJobDetail />
              </VeriCleanAuthProvider>
            } />
            <Route path="/vericlean/profile/edit" element={
              <VeriCleanAuthProvider>
                <VeriCleanProfileEdit />
              </VeriCleanAuthProvider>
            } />
            <Route path="/vericlean/cleaners/:cleanerId" element={
              <VeriCleanAuthProvider>
                <VeriCleanPublicProfile />
              </VeriCleanAuthProvider>
            } />
            <Route path="/vericlean/cleaners" element={
              <VeriCleanBrowseCleaners />
            } />
            <Route path="/vericlean/messages" element={
              <VeriCleanAuthProvider>
                <VeriCleanMessages />
              </VeriCleanAuthProvider>
            } />
            <Route path="/vericlean/verification" element={
              <VeriCleanAuthProvider>
                <VeriCleanVerification />
              </VeriCleanAuthProvider>
            } />
            {/* VeriClean VeriQuote Routes */}
            <Route path="/vericlean/quotes" element={
              <VeriCleanAuthProvider>
                <VeriCleanQuoteDashboard />
              </VeriCleanAuthProvider>
            } />
            <Route path="/vericlean/quotes/:id" element={
              <VeriCleanAuthProvider>
                <VeriCleanQuoteDetail />
              </VeriCleanAuthProvider>
            } />
            <Route path="/vericlean/invoices/:id" element={
              <VeriCleanAuthProvider>
                <VeriCleanInvoiceDetail />
              </VeriCleanAuthProvider>
            } />
            <Route path="/vericlean/rams/:id" element={
              <VeriCleanAuthProvider>
                <VeriCleanRamsDetail />
              </VeriCleanAuthProvider>
            } />
            <Route path="/vericlean/pricing" element={
              <VeriCleanAuthProvider>
                <VeriCleanPricing />
              </VeriCleanAuthProvider>
            } />
            <Route path="/vericlean/billing/success" element={
              <VeriCleanAuthProvider>
                <VeriCleanBillingSuccess />
              </VeriCleanAuthProvider>
            } />
            <Route path="/vericlean/settings" element={
              <VeriCleanAuthProvider>
                <VeriCleanSettings />
              </VeriCleanAuthProvider>
            } />
            <Route path="/vericlean/customers" element={
              <VeriCleanAuthProvider>
                <VeriCleanCustomersPage />
              </VeriCleanAuthProvider>
            } />
            <Route path="/vericlean/expenses" element={
              <VeriCleanAuthProvider>
                <VeriCleanExpensesPage />
              </VeriCleanAuthProvider>
            } />
            <Route path="/vericlean/tax" element={
              <VeriCleanAuthProvider>
                <VeriCleanTaxDashboardPage />
              </VeriCleanAuthProvider>
            } />
            <Route path="/vericlean/tax-reports" element={
              <VeriCleanAuthProvider>
                <VeriCleanTaxReportsPage />
              </VeriCleanAuthProvider>
            } />
            
            {/* Public Emergency Service Routes - NO ACCESS CODE REQUIRED */}
            <Route path="/veriassist" element={
              <PublicWithAuth>
                <VeriAssistPage />
              </PublicWithAuth>
            } />
            <Route path="/available-today" element={
              <PublicWithAuth>
                <AvailableTodayPage />
              </PublicWithAuth>
            } />
            
            {/* Public Supplier Notification Route */}
            <Route path="/supplier-notify/:leadId" element={<SupplierNotify />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/vericlean/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/vericlean/reset-password" element={<VeriCleanResetPassword />} />
            <Route path="/affiliate/forgot-password" element={<ForgotPasswordPage />} />
            
            {/* Supplier Landing Page - Separate from main user flow */}
            <Route path="/supplier" element={<SupplierLandingDispatcher />} />
            {/* P0 auth hotfix — dedicated modern supplier login & signup pages.
                Legacy ?login=true / ?signup=true on /supplier now redirect here via
                SupplierLandingDispatcher. These MUST be registered BEFORE any dynamic
                supplier route so the SPA never resolves them as a UUID or slug. */}
            <Route path="/supplier/login" element={<SupplierLoginV2 />} />
            <Route path="/supplier/signup" element={<SupplierSignupV2 />} />
            {/* Phase 3A — public tradesperson landing page (canonical route).
                Legacy synonyms redirect here permanently. */}
            <Route path="/trades" element={<TradeLandingV2 />} />
            <Route path="/for-trades" element={<Navigate to="/trades" replace />} />
            <Route path="/for-tradespeople" element={<Navigate to="/trades" replace />} />
            <Route path="/trade" element={<Navigate to="/trades" replace />} />
            
            {/* VeriSite Landing Page - Public route (V2 as of Phase VeriSite V2). */}
            <Route path="/verisite" element={<VeriSiteLandingV2 />} />
            
            {/* SEO Landing Pages — explicit routes for each slug */}
            {SEO_SLUGS.map(slug => (
              <Route key={slug} path={`/${slug}`} element={<SeoPageRouter />} />
            ))}
            
            {/* Protected App Routes - All wrapped in PrivateAccess */}
            <Route path="/*" element={
              <PrivateAccess>
                <AuthHandler />
              </PrivateAccess>
            } />
          </Routes>
        </Suspense>
        <Toaster position="top-right" />
      </BrowserRouter>
    </div>
    </HelmetProvider>
  );
}

export default App;
